import { all, put, takeLatest, select, call } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import {
  actions as macheteActions,
  utils as macheteReduxUtils,
} from 'src/machete-redux';
import EventNames from 'src/lib/eventNames';
import appDux from 'src/redux/app';
import jobDesignsDux from 'src/redux/jobDesigns';
import jobShareDux from 'src/redux/jobShare';
import macheteDux from 'src/redux/machete';
import measureModeDux from 'src/redux/measureMode';
import constants from 'src/constants';
import utils from 'src/utils';
import trackingDux from 'src/redux/tracking';
import splitDux from 'src/redux/split';
import GraphQLApi from 'src/apis/GraphQLApi';
import uiDux from './index';

const { isMobileDevice } = utils.breakpoint;

const {
  MODE_NAMES,
  PAINT_BRAND_ATTRIBUTE_PROPERTY,
  PREVIEW_MODE_NAME,
  SIDEBAR_TRANSITION_MS,
  SPLIT_MODES,
  ELEMENT_STYLE_ATTRIBUTES,
  ELEMENT_PAINT_ATTRIBUTES,
} = constants.app;
const { ORIGINAL_DESIGN_ID } = constants.jobDesign;
const { VIEW_OPTIONS } = constants.machete;
const { HIDE_IOS_WIDGETS_MESSAGE, SHOW_IOS_WIDGETS_MESSAGE } = constants.iosApp;
const {
  DESIGN_FEATURE_ACTIVATED,
  DESIGN_FEATURE_ADD_PAINT,
  DESIGN_FEATURE_CLICK_CANCEL_SAVE,
  DESIGN_FEATURE_CLICK_CONFIRM_SAVE,
  DESIGN_FEATURE_CLICK_SAVE,
  DESIGN_FEATURE_CLICK_ZOOM_IN,
  DESIGN_FEATURE_CLICK_ZOOM_OUT,
  DESIGN_FEATURE_DEACTIVATED,
  MEASUREMENTS_FEATURE_ACTIVATED,
  MEASUREMENTS_FEATURE_DEACTIVATED,
  CLICK_SCREENSHOT_MODE_FEATURE,
  CLICK_EXIT_SCREENSHOT_MODE_FEATURE,
  BUTTON_PRESSED,
  LINK_PRESSED,
  MODULE_VIEWED,
} = EventNames.viewer;

export function* handleAttributeButtonClickSaga({
  payload: { style, attribute },
}) {
  // track when users click "Paint Over"
  if (attribute.property === PAINT_BRAND_ATTRIBUTE_PROPERTY) {
    yield put(
      trackingDux.actions.track(DESIGN_FEATURE_ADD_PAINT, {
        materialId: style.materialId,
      }),
    );
  }

  yield put(appDux.actions.setActiveEditingStyle(style, attribute));
  yield put(uiDux.actions.openExtendedSidebar());
}

export function* handleSelectPreviousAttributeSaga() {
  const previousAttribute = yield select(
    appDux.selectors.getPreviousActiveAttribute,
  );

  if (previousAttribute == null) {
    // we're out of steps, going back will close the extended sidebar
    yield put(uiDux.actions.closeExtendedSidebar());
    return;
  }

  const style = yield select(appDux.selectors.getActiveEditingStyle);
  const attribute = yield select(appDux.selectors.getActiveEditingAttribute);
  const { nodes, ...styleAttributes } = style;
  let newNodeStyles = { ...styleAttributes };

  if (attribute.property === 'paintColorId' && !style.paintColorId) {
    const clearPaintUpdate = {
      paintBrandId: '',
      paintProductId: '',
    };
    newNodeStyles = { ...newNodeStyles, ...clearPaintUpdate };

    yield put(appDux.actions.replaceNodeStyles(nodes, newNodeStyles));
  }

  const ATTR_LIST = previousAttribute.includes('paint')
    ? ELEMENT_PAINT_ATTRIBUTES
    : ELEMENT_STYLE_ATTRIBUTES;
  const nextAttribute = ATTR_LIST.find(
    (attr) => attr.property === previousAttribute,
  );
  yield put(
    appDux.actions.setActiveEditingStyle(
      { nodes, ...newNodeStyles },
      nextAttribute,
    ),
  );
}

export function* setNativeWidgetVisibility(isVisible) {
  const message = isVisible
    ? SHOW_IOS_WIDGETS_MESSAGE
    : HIDE_IOS_WIDGETS_MESSAGE;

  const { isIosWebView, isReactNativeWebView } = yield select(
    (state) => state.designPro.app,
  );

  // this method is for the native apps (white label apps)
  // this format is important, this call needs the context (https://redux-saga.js.org/docs/api/)
  // otherwise it throws an error please don't try to refactor
  if (isIosWebView) {
    const callArgs = [
      window.webkit.messageHandlers.jsbridge,
      window.webkit.messageHandlers.jsbridge.postMessage,
    ];
    yield call(callArgs, message);
  }

  const { isNativeApp } = yield select((state) => state.designPro.ui);
  // this is suppose to work for both the android and ios react native app
  // but currently only works for IOS
  if (isReactNativeWebView && !isNativeApp) {
    // See issue https://github.com/react-native-webview/react-native-webview/issues/323
    // window.ReactNativeWebView.postMessage loses its binding when passed into
    // call so it must be binded here.
    yield call(
      window.ReactNativeWebView.postMessage.bind(window.ReactNativeWebView),
      message,
    );
  }
}

export function* handleDesignModeClickSaga() {
  yield call(setNativeWidgetVisibility, false);
  const jobDesigns = yield select(jobDesignsDux.selectors.getJobDesigns);
  const selectedDesignId = yield select(
    jobDesignsDux.selectors.getSelectedDesignId,
  );
  const isOriginalDesignSelected = selectedDesignId === ORIGINAL_DESIGN_ID;
  const hasSavedDesign = jobDesigns.saved[0];

  if (isOriginalDesignSelected && hasSavedDesign && !isMobileDevice()) {
    yield put(uiDux.actions.updateIsEditOriginalModalOpen(true));
    return;
  }

  yield put(trackingDux.actions.track(DESIGN_FEATURE_ACTIVATED));

  yield put(appDux.actions.setActiveMode(MODE_NAMES.DESIGN));
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(uiDux.actions.openSidebar());
}

export function* handleScreenshotModeClickSaga() {
  yield call(setNativeWidgetVisibility, false);
  // remove outlines from selected nodes
  const visibleNodes = yield select(macheteDux.selectors.getVisibleNodes);
  visibleNodes.forEach(({ uuid }) => {
    const node = macheteReduxUtils.getNode(uuid);
    const { currentModelElements = [] } = node;
    currentModelElements.forEach((el) => {
      el.includeOutline = false;
    });
  });

  // add tracking that screenshot mode was entered and from where
  const activeMode = yield select(appDux.selectors.getActiveMode);
  const reportedMode = activeMode || PREVIEW_MODE_NAME;
  yield put(
    trackingDux.actions.track(CLICK_SCREENSHOT_MODE_FEATURE, {
      viewer_mode: reportedMode,
    }),
  );
  yield put(appDux.actions.setIsScreenshotModeActive(true));
}

export function* handleMeasureModeClickSaga() {
  yield call(setNativeWidgetVisibility, false);
  macheteReduxUtils.onlyOutlineAllFacades();
  yield put(measureModeDux.actions.activateMeasureMode());

  yield put(trackingDux.actions.track(MEASUREMENTS_FEATURE_ACTIVATED));

  yield put(appDux.actions.setActiveMode(MODE_NAMES.MEASURE));
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(uiDux.actions.openSidebar());
  yield put(macheteDux.actions.disable2od());
}

export function* handleSidebarCloseClickSaga() {
  yield call(setNativeWidgetVisibility, true);
  const activeMode = yield select(appDux.selectors.getActiveMode);
  const isSelectToolActive = yield select(appDux.selectors.isSelectToolActive);

  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(uiDux.actions.closeSidebar());
  macheteReduxUtils.setDefaultVisibility();
  yield put(macheteActions.segmentation.setNodeSelection([]));

  if (activeMode === MODE_NAMES.DESIGN) {
    yield put(trackingDux.actions.track(DESIGN_FEATURE_DEACTIVATED));
  } else {
    yield put(trackingDux.actions.track(MEASUREMENTS_FEATURE_DEACTIVATED));
    yield put(macheteDux.actions.enable2od());
  }

  // delay setting mode to null to allow sidebar to transition to closed
  yield delay(SIDEBAR_TRANSITION_MS);
  yield put(appDux.actions.setActiveMode(null));

  if (isSelectToolActive) {
    yield put(appDux.actions.setIsSelectToolActive(false));
  }
}

export function* handleViewOptionClickSaga(action) {
  const { viewName } = action.payload;

  if (viewName === VIEW_OPTIONS.zoomIn.id) {
    yield put(macheteDux.actions.zoomIn());
    yield put(trackingDux.actions.track(DESIGN_FEATURE_CLICK_ZOOM_IN));
  } else if (viewName === VIEW_OPTIONS.zoomOut.id) {
    yield put(macheteDux.actions.zoomOut());
    yield put(trackingDux.actions.track(DESIGN_FEATURE_CLICK_ZOOM_OUT));
  } else {
    yield put(macheteDux.actions.panCamera(viewName));
  }
}

function* handleCloseJobShareClickSaga() {
  yield put(uiDux.actions.updateJobShareModalOpen(false));
  yield put(jobShareDux.actions.updateIsJobShareLoading(false));
  yield put(jobShareDux.actions.receiveJobShareData(null));
  yield put(jobShareDux.actions.resetJobShareEmailState());
}

export function* handleShareClickSaga() {
  const { SHARE_FEATURE_CLICK } = EventNames.viewer.share;
  yield put(trackingDux.actions.track(SHARE_FEATURE_CLICK));
  yield put(jobShareDux.actions.createJobShare());
}

export function* handleSendJobShareEmailClickSaga(actions) {
  const { email, senderText } = actions.payload;
  yield put(jobShareDux.actions.sendJobShareEmail(email, senderText));
}

export function* handleUpdateJobShareEmailStateSaga(actions) {
  const { emailState } = actions.payload;
  yield put(jobShareDux.actions.updateJobShareEmailState(emailState));
}

function* updateSelectedElementsByAttributeTypeSaga({ payload }) {
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(appDux.actions.setBreadcrumbData(payload));
  yield put(appDux.actions.selectElementsByAttribute({ ...payload }));
}

function* handleMeasurementCategoryClickSaga(action) {
  const { categoryName } = action.payload;
  yield put(measureModeDux.actions.setActiveCategoryName(categoryName));
}

function* handleCloseMeasureExtendedSidebarClickSaga() {
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(measureModeDux.actions.resetActiveCategoryName());
}

function* resetSelectedElementsSaga() {
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(appDux.actions.setBreadcrumbData({}));
  yield put(macheteActions.segmentation.clearNodeSelection());
}

function* handleSaveDesignButtonClickSaga() {
  yield put(uiDux.actions.updateSaveConfirmationModalOpen(true));
  yield put(trackingDux.actions.track(DESIGN_FEATURE_CLICK_SAVE));
}

function* handleCancelSaveConfirmationClickSaga() {
  yield put(uiDux.actions.updateSaveConfirmationModalOpen(false));
  yield put(trackingDux.actions.track(DESIGN_FEATURE_CLICK_CANCEL_SAVE));
}

function* handleCloseSaveConfirmationClickSaga() {
  yield put(uiDux.actions.updateSaveConfirmationModalOpen(false));
}

export function* handleCloseScreenshotModeClickSaga() {
  const activeMode = yield select(appDux.selectors.getActiveMode);
  if (!activeMode) {
    yield call(setNativeWidgetVisibility, true);
  }
  yield put(trackingDux.actions.track(CLICK_EXIT_SCREENSHOT_MODE_FEATURE));
  // re-outline selected nodes because we want to keep them when entering screenshot mode
  yield put(macheteDux.actions.outlineSelectedElements());
  yield put(macheteActions.segmentation.enablePointer());
  yield put(appDux.actions.setIsScreenshotModeActive(false));
}

function* handleConfirmSaveClickSaga() {
  yield put(uiDux.actions.updateSaveConfirmationModalOpen(false));
  yield put(jobDesignsDux.actions.saveDesign());
  yield put(trackingDux.actions.track(DESIGN_FEATURE_CLICK_CONFIRM_SAVE));
}

function* handleSelectDesignClickSaga(action) {
  const { designId } = action.payload;

  yield put(jobDesignsDux.actions.selectDesign(designId));
}

function* handleEditOriginalConfirmClickSaga() {
  yield put(uiDux.actions.updateIsEditOriginalModalOpen(false));
  yield put(appDux.actions.setActiveMode(MODE_NAMES.DESIGN));
  yield put(uiDux.actions.openSidebar());
}

function* handleEditOriginalCloseClickSaga() {
  yield put(uiDux.actions.updateIsEditOriginalModalOpen(false));
}

function* handleDeselectAllClickSaga() {
  yield put(uiDux.actions.closeExtendedSidebar());
  yield put(macheteActions.segmentation.clearNodeSelection());
}

function* handleSelectToolButtonClickSaga() {
  const isSelectToolActive = yield select(appDux.selectors.isSelectToolActive);
  yield put(appDux.actions.setIsSelectToolActive(!isSelectToolActive));
}

function* handleSwitchOrientationSaga() {
  const isOrientationChangeEnabled = yield select(
    splitDux.selectors.isOrientationChangeEnabled,
  );
  const orientation = yield select(splitDux.selectors.getSplitOrientation);

  if (!isOrientationChangeEnabled) return;

  const newOrientation =
    orientation === SPLIT_MODES.HORIZONTAL
      ? SPLIT_MODES.VERTICAL
      : SPLIT_MODES.HORIZONTAL;

  yield put(splitDux.actions.changeSplitToolOrientation(newOrientation));
}

function* handleRenderingLinksClicked({ payload: { linkText, trackId } }) {
  const { id } = yield select(appDux.selectors.getJobDetails);

  yield put(
    trackingDux.actions.track(LINK_PRESSED, {
      feature: 'rendering',
      button_text: linkText,
      job_id: id,
      track_id: trackId,
    }),
  );
}

export function* handleRenderingClickSaga({ payload: { btnText } }) {
  const { id } = yield select(appDux.selectors.getJobDetails);
  const { isReactNativeWebView } = yield select((state) => state.designPro.app);

  yield put(
    trackingDux.actions.track(BUTTON_PRESSED, {
      feature: 'rendering',
      track_id: 'rendering-cta',
      button_text: btnText,
      job_id: id,
    }),
  );

  if (isReactNativeWebView) {
    const url = yield call([GraphQLApi, GraphQLApi.createHiDefRendering], id);
    const payload = JSON.stringify({ action: 'openLink', data: url });
    return yield call(
      // See issue https://github.com/react-native-webview/react-native-webview/issues/323
      window.ReactNativeWebView.postMessage.bind(window.ReactNativeWebView),
      payload,
    );
  }

  const formWindow = window.open('', 'hi_def_rendering_form');
  formWindow.document.write('Loading form...');

  const requestFormUrl = yield call(
    [GraphQLApi, GraphQLApi.createHiDefRendering],
    id,
  );

  if (requestFormUrl) {
    formWindow.location.href = requestFormUrl;
  } else {
    formWindow.close();
    alert('Error creating rendering request');
  }
}

export default function* sagas() {
  yield all([
    takeLatest(
      [
        uiDux.actions.handleDesignCategoryClick,
        uiDux.actions.handleDesignMaterialClick,
      ],
      updateSelectedElementsByAttributeTypeSaga,
    ),
    takeLatest(uiDux.actions.handleAllProductsClick, resetSelectedElementsSaga),
    takeLatest(
      uiDux.actions.handleCancelSaveConfirmationClick,
      handleCancelSaveConfirmationClickSaga,
    ),
    takeLatest(
      uiDux.actions.handlePreviousAttributeButtonClick,
      handleSelectPreviousAttributeSaga,
    ),
    takeLatest(
      uiDux.actions.handleCloseJobShareClick,
      handleCloseJobShareClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleCloseMeasureExtendedSidebarClick,
      handleCloseMeasureExtendedSidebarClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleCloseSaveConfirmationClick,
      handleCloseSaveConfirmationClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleConfirmSaveClick,
      handleConfirmSaveClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleDeselectAllClick,
      handleDeselectAllClickSaga,
    ),
    takeLatest(uiDux.actions.handleDesignModeClick, handleDesignModeClickSaga),
    takeLatest(
      uiDux.actions.handleScreenshotModeClick,
      handleScreenshotModeClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleCloseScreenshotModeClick,
      handleCloseScreenshotModeClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleEditOriginalCloseClick,
      handleEditOriginalCloseClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleEditOriginalConfirmClick,
      handleEditOriginalConfirmClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleMeasurementCategoryClick,
      handleMeasurementCategoryClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleSelectToolButtonClick,
      handleSelectToolButtonClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleMeasureModeClick,
      handleMeasureModeClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleSaveDesignButtonClick,
      handleSaveDesignButtonClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleSelectDesignClick,
      handleSelectDesignClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleSendJobShareEmailClick,
      handleSendJobShareEmailClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleUpdateJobShareEmailState,
      handleUpdateJobShareEmailStateSaga,
    ),
    takeLatest(uiDux.actions.handleShareClick, handleShareClickSaga),
    takeLatest(
      uiDux.actions.handleSidebarCloseClick,
      handleSidebarCloseClickSaga,
    ),
    takeLatest(
      uiDux.actions.handleSwitchSplitOrientationClick,
      handleSwitchOrientationSaga,
    ),
    takeLatest(
      uiDux.actions.handleStyleAttributeButtonClick,
      handleAttributeButtonClickSaga,
    ),
    takeLatest(uiDux.actions.handleViewOptionClick, handleViewOptionClickSaga),
    takeLatest(
      uiDux.actions.handleRenderingLinksClick,
      handleRenderingLinksClicked,
    ),
    takeLatest(
      uiDux.actions.handleRenderingCreateClick,
      handleRenderingClickSaga,
    ),
  ]);
}
